import format from "date-fns/format"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { FormProvider } from "react-hook-form"
import { useMutation } from "react-query"
import FuelDockAvailability from "src/main/Settings/PublicProfile/Fuel/FuelDockAvailability"
import FuelMeasurementUnitSelector from "src/main/Settings/PublicProfile/Fuel/FuelMeasurementUnitSelector"
import FuelRatesForm from "src/main/Settings/PublicProfile/Fuel/FuelRatesForm"
import { useFuelDockForm } from "src/main/Settings/PublicProfile/Fuel/useFuelDockForm"

import Button from "src/components/Button"
import Form from "src/components/Form"
import HeadingPageTitle from "src/components/Heading/HeadingPageTitle"

import { updatePublicProfileFuel } from "src/api/Settings/PublicProfile/Fuel"

import { useToast } from "src/hooks/use_toast"

const PublicProfileFuel = ({
  fuelOptions,
  fuelDockAvailability,
  header,
  salesLink,
  saveButtonCopy,
  ratesHeader,
  currentFuelPriceRequestCount,
  fuelPricesStale,
  fuelMeasurementUnit,
  fuelMeasurementUnitSettingsEnabled,
  fuelMeasurementUnits,
}) => {
  const showToast = useToast()
  const methods = useFuelDockForm({
    fuelDockAvailability,
    fuelOptions,
    fuelMeasurementUnit,
  })
  const [showAlertBanner, setShowAlertBanner] = useState(true)

  const { mutate: submitForm, isLoading: submittingForm } = useMutation(
    (data) => updatePublicProfileFuel({ data }),
    {
      onSuccess: (data) => {
        showToast(data.message, { type: "success" })
        setShowAlertBanner(false)
      },
      onError: (error) => {
        showToast(error.message, { type: "error" })
      },
    }
  )

  const formatTimeValue = (value) => {
    try {
      return format(value, "HH:mm")
    } catch {
      return ""
    }
  }

  const onSubmit = (data) => {
    const formattedData = {
      fuel_dock_availability: {
        enabled: data.fuelDockAvailability.enabled,
        season: {
          year_round: data.fuelDockAvailability.season.yearRound,
          start_month: data.fuelDockAvailability.season.startMonth,
          start_day: data.fuelDockAvailability.season.startDay,
          end_month: data.fuelDockAvailability.season.endMonth,
          end_day: data.fuelDockAvailability.season.endDay,
        },
        days: Object.entries(data.fuelDockAvailability.days).reduce(
          (acc, [day, { enabled, timeRange }]) => {
            acc[day] = {
              enabled,
              time_range: {
                start: timeRange.start ? formatTimeValue(timeRange.start) : "",
                end: timeRange.start ? formatTimeValue(timeRange.end) : "",
              },
            }
            return acc
          },
          {}
        ),
      },
      manage_public_profile_fuel_decorator: {
        gas_regular_price: data.fuelOptions["Regular Gas"],
        gas_super_price: data.fuelOptions["Super Gas"],
        diesel_price: data.fuelOptions.Diesel,
        propane_price: data.fuelOptions.Propane,
        gas_premium_price: data.fuelOptions["Premium Gas"],
      },
      fuel_measurement_unit: data.fuelMeasurementUnit,
    }

    submitForm(formattedData)
  }

  return (
    <FormProvider {...methods}>
      <div className="mt-8">
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="mb-4 flex flex-col justify-between lg:flex-row lg:items-center">
            <div className="flex flex-col space-y-2">
              <HeadingPageTitle>{header}</HeadingPageTitle>
              <div className="flex space-x-1">
                <span>Fuel rates will also be updated on your</span>
                <a
                  href={salesLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-blue-600"
                >
                  Sales
                </a>
                <span>page</span>
              </div>
            </div>
            <div className="mt-2 lg:mt-0">
              <Button
                variant="primary"
                type="submit"
                isLoading={submittingForm}
                disabled={!methods.formState.isDirty}
              >
                {saveButtonCopy}
              </Button>
            </div>
          </div>
          <FuelMeasurementUnitSelector
            fuelMeasurementUnitSettingsEnabled={
              fuelMeasurementUnitSettingsEnabled
            }
            fuelMeasurementUnits={fuelMeasurementUnits}
          />
          <FuelRatesForm
            ratesHeader={ratesHeader}
            fuelOptions={fuelOptions}
            currentFuelPriceRequestCount={currentFuelPriceRequestCount}
            fuelPricesStale={fuelPricesStale}
            showAlertBanner={showAlertBanner}
            setShowAlertBanner={setShowAlertBanner}
          />
          <FuelDockAvailability />
        </Form>
      </div>
    </FormProvider>
  )
}

PublicProfileFuel.propTypes = {
  fuelOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string,
    })
  ).isRequired,
  fuelDockAvailability: PropTypes.shape({
    season: PropTypes.shape({
      yearRound: PropTypes.bool,
      startMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      startDay: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      endMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      endDay: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    enabled: PropTypes.bool,
    days: PropTypes.object,
  }),
  header: PropTypes.string.isRequired,
  salesLink: PropTypes.string.isRequired,
  saveButtonCopy: PropTypes.string.isRequired,
  ratesHeader: PropTypes.string.isRequired,
  currentFuelPriceRequestCount: PropTypes.number.isRequired,
  fuelPricesStale: PropTypes.bool,
  fuelMeasurementUnit: PropTypes.string.isRequired,
  fuelMeasurementUnitSettingsEnabled: PropTypes.bool.isRequired,
  fuelMeasurementUnits: PropTypes.arrayOf(PropTypes.string).isRequired,
}

export default PublicProfileFuel
